import { Injector, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { BaseService } from '../base/base.service';
import { Query } from '../../models/query/query';
import { Nabavka } from '../../models/nabavke/nabavka';
import { AppModulePath } from '../../models/app-module-path';
import { WebMethodResult } from '../../models/web-method-result';
import { Contract } from 'app/models/nabavke/contract';
import { HttpClient } from '@angular/common/http';
import { PlatformLocation } from '@angular/common';
import { ContractService } from './contract.service';
import { catchError, map } from 'rxjs/operators';
import { Criterion } from 'app/models/query/criterion';


@Injectable()
export class NabavkaService extends BaseService {
  private napraviSituacijuPonuda = '/api/nabavka/napraviSituaciju';
  private listUrl = '/api/nabavka/list';
  private listYearsUrl = '/api/nabavka/listYears';
  private countUrl = '/api/nabavka/count';
  private filterCountUrl = '/api/nabavka/filterCount';
  private saveUrl = '/api/nabavka/save';
  private createSameUrl = '/api/nabavka/createSame';
  private deleteUrl = '/api/nabavka/delete/';
  private findByIdUrl = '/api/nabavka/findById';
  private getNabavkaChart = '/api/nabavka/nabavkaChart';
  private realizacijaAktivnihProjekataUrl = '/api/nabavka/realizacijaAktivnihProjekata';
  private getVrednostUgovoraChart = '/api/nabavka/vrednostUgovoraChart';
  private getNabavkaFazeCharData = '/api/nabavka-faze/getTimelineData/';

  private importPonudaUrl = '/api/nabavka/import_ponuda';
  private importPonudaSubcontractor = '/api/nabavka/import_subcontractor';

  constructor(protected injector: Injector,
              protected http2: HttpClient,
              protected platformLocation: PlatformLocation,
              private contractSvc: ContractService) {
    super(injector, http2, platformLocation);
  }

  list(qry: Query): Observable<Nabavka[]> {
    const url = this.listUrl + '?q=' + encodeURIComponent(JSON.stringify(qry));
    return this.get<Nabavka[]>(url, AppModulePath.JAVNE_NABAVKE).pipe(
      map(this.extractData),
      catchError(this.handleError));
  }

  listYears(): Observable<number[]> {
    return this.get<number[]>(this.listYearsUrl, AppModulePath.JAVNE_NABAVKE).pipe(
      map(res => res),
      catchError(this.handleError));
  }

  count(qry: Query): Observable<number> {
    const url = this.countUrl + '?q=' + encodeURIComponent(JSON.stringify(qry));
    return this.get<number>(url, AppModulePath.JAVNE_NABAVKE).pipe(
      map(res => res),
      catchError(this.handleError));
  }

  filterCount(query: Query): Observable<number> {
    const url = this.filterCountUrl + '?q=' + encodeURIComponent(JSON.stringify(query));
    return this.get<number>(url, AppModulePath.JAVNE_NABAVKE).pipe(
      map(res => res),
      catchError(this.handleError));
  }

  save(nabavka: Nabavka): Observable<Nabavka> {
    return this.post<Nabavka>(this.saveUrl, AppModulePath.JAVNE_NABAVKE, nabavka).pipe(
      map(res => {
        const data = this.extractData(res);
        return data.length > 0 ? data[0] : null;
      }),
      catchError(this.handleError));
  }

  createSame(nabavka: Nabavka): Observable<Nabavka> {
    return this.post<Nabavka>(this.createSameUrl, AppModulePath.JAVNE_NABAVKE, nabavka).pipe(
      map(res => {
        const data = this.extractData(res);
        return data.length > 0 ? data[0] : null;
      }),
      catchError(this.handleError));
  }

  delete(id: string): Observable<WebMethodResult> {
    return this._delete<WebMethodResult>(this.deleteUrl + id, AppModulePath.JAVNE_NABAVKE).pipe(
      map(res => new WebMethodResult(res)),
      catchError(this.handleError));
  }

  findById(id: string): Observable<Nabavka> {
    return this.get<Nabavka>(this.findByIdUrl + '/' + id, AppModulePath.JAVNE_NABAVKE).pipe(
      map(res => {
        const data = this.extractData(res);
        return data.length > 0 ? data[0] : null;
      }),
      catchError(this.handleError));
  }

  extractData(res: any): Nabavka[] {
    const data = res || [];
    if (data instanceof Array) {
      return data.map((d) => {
        const nab = new Nabavka(d);
        nab.pokretanjePostupka = new Date(d.pokretanjePostupka);
        nab.zakljucenjaUgovora = new Date(d.zakljucenjaUgovora);
        nab.izvrsenjaUgovora = new Date(d.izvrsenjaUgovora);
        return nab;
      });
    } else {
      const nab = new Nabavka(data);
      nab.pokretanjePostupka = new Date(data.pokretanjePostupka);
      nab.zakljucenjaUgovora = new Date(data.zakljucenjaUgovora);
      nab.izvrsenjaUgovora = new Date(data.izvrsenjaUgovora);
      return [nab];
    }
  }

  export(reportName: String, query: Query): any {
    return this.downloadFile('/api/nabavka/report/' + reportName, AppModulePath.JAVNE_NABAVKE, query).pipe(
      map((res) => {
        return new Blob([res], { type: 'application/pdf' });
      }),
      catchError(this.handleError));
  }

  exportCSV(reportName: String, query: Query): any {
    return this.downloadFile('/api/nabavka/report/' + reportName, AppModulePath.JAVNE_NABAVKE, query).pipe(
      map((res) => {
        return new Blob([res], { type: 'text/csv' });
      }),
      catchError(this.handleError));
  }

  getChartData(data: any): any {
    const params = `?zakon=${encodeURIComponent(data.zakon)}&dateStart=${encodeURIComponent(data.dateStart)}&dateEnd=${encodeURIComponent(data.dateEnd)}&vrsta=${encodeURIComponent(data.vrsta)}`;
    return this.get(this.getNabavkaChart + params, AppModulePath.JAVNE_NABAVKE).pipe(
      map(this.extractData),
      catchError(this.handleError));
  }

  getVrednostUgovoraData(data: any): Observable<Contract[]> {
    const params = `?dateStart=${encodeURIComponent(data.dateStart)}&dateEnd=${encodeURIComponent(data.dateEnd)}&procenat=${encodeURIComponent(data.procenat)}`;
    return this.get<Contract[]>(this.getVrednostUgovoraChart + params, AppModulePath.JAVNE_NABAVKE).pipe(
      map(res => this.contractSvc.extractData(res)),
      catchError(this.handleError));
  }

  exportWithParam(reportName: String, data: any): any {
    return this.downloadFile('/api/nabavka/report/' + reportName, AppModulePath.JAVNE_NABAVKE, data).pipe(
      map((res) => {
        if (res.type === 'application/json') {
          return res;
        } else {
          return new Blob([res], { type: 'application/pdf' });
        }
      }),
      catchError(this.handleError));
  }

  getNabavakaFazeChartData(data: any): Observable<any[]> {
    return this.post<any[]>(this.getNabavkaFazeCharData, AppModulePath.CONTRACT, data).pipe(
      map(res => res),
      catchError(this.handleError));
  }

  importPonuda(ponudaId: string, csvPonuda: File) {
    const fd: FormData = new FormData();
    fd.append('id', ponudaId);
    fd.append('file', csvPonuda);
    return this.put<WebMethodResult>(this.importPonudaUrl, AppModulePath.JAVNE_NABAVKE, fd).pipe(
      map(res => res),
      catchError(this.handleError));
  }

  importSubcontractor(subcontractorId: string, file: File) {
  const fd: FormData = new FormData();
  fd.append('id', subcontractorId); 
  fd.append('file', file);

  return this.put<WebMethodResult>(this.importPonudaSubcontractor, AppModulePath.JAVNE_NABAVKE, fd).pipe(
    map(res => res),
    catchError(this.handleError)
  );
}

  realizacijaAktivnihProjekata() {
    return this.get<any[]>(this.realizacijaAktivnihProjekataUrl, AppModulePath.JAVNE_NABAVKE).pipe(
      map(res => res),
      catchError(this.handleError));
  }

  createSistuaciju(ponudaId: string): Observable<WebMethodResult> {
    const url = `${this.napraviSituacijuPonuda}/${ponudaId}`;
    return this.post<WebMethodResult>(url, AppModulePath.PROIZVODNJA_WORK_ORDER, null).pipe(
      map(res => new WebMethodResult(res)),
      catchError(this.handleError)
    );
  }

  getNabavke(): Observable<Nabavka[]> {
    const url = '/api/nabavka/list?q=' + encodeURIComponent(JSON.stringify({}));
    return this.get<Nabavka[]>(url, AppModulePath.JAVNE_NABAVKE).pipe(
      map(this.extractData),
      catchError(this.handleError)
    );
  }
exportToExcel(
  reportPath: string,
  query: Query,
  filename: string,
  includePrice: boolean
) {
  let url = `/api/${reportPath}`;

  if (includePrice !== undefined) {
    url += `?includePrice=${includePrice}`;
  }

  return this.downloadFile(url, AppModulePath.JAVNE_NABAVKE, query).pipe(
    map(res => res),
    catchError(this.handleError)
  );
}








}
